import { isStaffSurface } from '@titan/shared';
import { useEffect, useState } from 'react';
import { config } from '../config.js';
import { useSession } from '../session/SessionProvider.js';
import { ScreenBody, ScreenHeader } from '../shell/Shell.js';
import { Button, Card, InlineError } from '../ui/index.js';
import { errorMessage } from './useLoad.js';

/**
 * The designed prototype, hosted as-is for DinoCloud staff. The API sets CloudFront
 * signed cookies for `/demo/*`; locally Vite serves the copy under `public/demo`.
 */
export function Demo() {
  const { me, api } = useSession();
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [error, setError] = useState<string | null>(null);
  const staff = me ? isStaffSurface(me.actor) : false;

  useEffect(() => {
    if (!staff) return;
    api<{ ok: boolean }>('/demo/access')
      .then(() => setState('ready'))
      .catch((err: unknown) => {
        setError(errorMessage(err));
        setState('error');
      });
  }, [api, staff]);

  if (!staff) {
    return (
      <>
        <ScreenHeader eyebrow="demo" title="Demo" />
        <ScreenBody>
          <Card title="Staff only" description="The demo shows the designed console with sample data. It is available to DinoCloud staff on their own surface." />
        </ScreenBody>
      </>
    );
  }

  return (
    <>
      <ScreenHeader
        eyebrow="demo · designed experience"
        title="Demo"
        description="The full console as designed, with sample data. Nothing here is connected to a real tenant. Use the state rail at the top to jump between screens."
        actions={
          <Button variant="secondary" onClick={() => window.open(config.demoPath, '_blank', 'noopener')} disabled={state !== 'ready'}>
            Open in a new tab
          </Button>
        }
      />
      {state === 'error' && (
        <ScreenBody>
          <InlineError>{error}</InlineError>
        </ScreenBody>
      )}
      {state === 'ready' && (
        <iframe
          title="Titan prototype"
          src={config.demoPath}
          style={{ border: 0, width: '100%', flex: 1, minHeight: 'calc(100vh - var(--sp-72) * 2)' }}
          sandbox="allow-scripts allow-same-origin allow-popups"
        />
      )}
    </>
  );
}
